import React, { useEffect, useMemo, useState } from 'react';
import { FixedAsset, FixedAssetFormValues, KamokuOption } from './fixedAssetTypes';

interface FixedAssetFormProps {
  mode: 'create' | 'edit';
  asset: FixedAsset | null;
  kamokuOptions: KamokuOption[];
  onSubmit: (values: FixedAssetFormValues, assetId?: number | null) => Promise<void>;
  onCancelEdit?: () => void;
  loading?: boolean;
}

const defaultValues: FixedAssetFormValues = {
  asset_name: '',
  acquisition_date: '',
  acquisition_cost: '',
  useful_life_years: '5',
  location: '',
  depreciation_method: '定額法',
  service_start_date: '',
  kamoku_id: '',
  remarks: '',
};

interface Suggestion {
  usefulLife?: string;
  kamokuId?: string;
  reason?: string;
}

const computeSuggestion = (assetName: string, kamokuOptions: KamokuOption[]): Suggestion => {
  const normalized = assetName.toLowerCase();
  const rules = [
    {
      keywords: ['車', '車両', '自動車', 'バス', 'トラック'],
      usefulLife: '6',
      kamokuKeywords: ['車両', '車', '運搬'],
      reason: '車両・自動車カテゴリと判断',
    },
    {
      keywords: ['建物', '園舎', '園庭', '施設'],
      usefulLife: '20',
      kamokuKeywords: ['建物'],
      reason: '建物・園舎カテゴリと判断',
    },
    {
      keywords: ['パソコン', 'pc', 'コンピュータ', 'ノート', 'タブレット'],
      usefulLife: '4',
      kamokuKeywords: ['備品', '器具'],
      reason: '情報機器カテゴリと判断',
    },
    {
      keywords: ['プリンタ', '複合機', 'コピー'],
      usefulLife: '5',
      kamokuKeywords: ['備品', '器具'],
      reason: '事務機器カテゴリと判断',
    },
    {
      keywords: ['机', '椅子', '什器', '棚', 'ロッカー', '備品'],
      usefulLife: '5',
      kamokuKeywords: ['備品'],
      reason: '備品カテゴリと判断',
    },
    {
      keywords: ['空調', 'エアコン'],
      usefulLife: '6',
      kamokuKeywords: ['備品', '設備'],
      reason: '空調設備カテゴリと判断',
    },
  ];

  for (const rule of rules) {
    if (rule.keywords.some((keyword) => normalized.includes(keyword))) {
      const matchedKamoku = kamokuOptions.find((option) =>
        rule.kamokuKeywords.some((kw) => option.name.includes(kw)),
      );
      return {
        usefulLife: rule.usefulLife,
        kamokuId: matchedKamoku ? String(matchedKamoku.id) : undefined,
        reason: rule.reason,
      };
    }
  }

  const defaultKamoku = kamokuOptions.find((option) =>
    option.name.includes('減価償却費') || option.name.includes('備品'),
  );

  return {
    usefulLife: undefined,
    kamokuId: defaultKamoku ? String(defaultKamoku.id) : undefined,
    reason: defaultKamoku ? '標準の減価償却費科目を選択' : undefined,
  };
};

const FixedAssetForm: React.FC<FixedAssetFormProps> = ({
  mode,
  asset,
  kamokuOptions,
  onSubmit,
  onCancelEdit,
  loading = false,
}) => {
  const [values, setValues] = useState<FixedAssetFormValues>(defaultValues);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState<{ usefulLife: boolean; kamoku: boolean }>({
    usefulLife: false,
    kamoku: false,
  });
  const [suggestReason, setSuggestReason] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (mode === 'edit' && asset) {
      setValues({
        asset_name: asset.asset_name,
        acquisition_date: asset.acquisition_date ?? '',
        acquisition_cost: String(asset.acquisition_cost ?? ''),
        useful_life_years: String(asset.useful_life_years ?? ''),
        location: asset.location ?? '',
        depreciation_method: asset.depreciation_method ?? '定額法',
        service_start_date: asset.service_start_date ?? '',
        kamoku_id: asset.kamoku_id ? String(asset.kamoku_id) : '',
        remarks: asset.remarks ?? '',
      });
      setTouched({ usefulLife: true, kamoku: true });
      setSuggestReason(undefined);
      setError(null);
      setSuccess(null);
    } else if (mode === 'create') {
      setValues(defaultValues);
      setTouched({ usefulLife: false, kamoku: false });
      setSuggestReason(undefined);
      setError(null);
      setSuccess(null);
    }
  }, [mode, asset]);

  const suggestion = useMemo(() => {
    if (!values.asset_name.trim()) {
      return null;
    }
    return computeSuggestion(values.asset_name.trim(), kamokuOptions);
  }, [values.asset_name, kamokuOptions]);

  useEffect(() => {
    if (!values.asset_name.trim() || mode === 'edit') {
      return;
    }
    if (!touched.usefulLife && suggestion?.usefulLife) {
      setValues((prev) => ({
        ...prev,
        useful_life_years: suggestion.usefulLife ?? prev.useful_life_years,
      }));
      setSuggestReason(suggestion.reason);
    }
    if (!touched.kamoku && suggestion?.kamokuId) {
      setValues((prev) => ({
        ...prev,
        kamoku_id: suggestion.kamokuId ?? prev.kamoku_id,
      }));
      setSuggestReason(suggestion.reason);
    }
  }, [suggestion, touched.usefulLife, touched.kamoku, mode, values.asset_name]);

  useEffect(() => {
    if (!values.acquisition_date) {
      return;
    }
    setValues((prev) => {
      if (prev.service_start_date) {
        return prev;
      }
      return { ...prev, service_start_date: prev.acquisition_date };
    });
  }, [values.acquisition_date]);

  const handleChange = (field: keyof FixedAssetFormValues, value: string) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (field === 'useful_life_years') {
      setTouched((prev) => ({ ...prev, usefulLife: true }));
    }
    if (field === 'kamoku_id') {
      setTouched((prev) => ({ ...prev, kamoku: true }));
    }
    if (field === 'asset_name') {
      setSuggestReason(undefined);
    }
  };

  const validate = (): string | null => {
    if (!values.asset_name.trim()) {
      return '資産名称は必須です。';
    }
    if (!values.acquisition_date) {
      return '取得日を入力してください。';
    }
    if (!values.service_start_date) {
      return '供用開始日を入力してください。';
    }
    if (values.service_start_date < values.acquisition_date) {
      return '供用開始日は取得日以降の日付にしてください。';
    }
    const cost = Number(values.acquisition_cost);
    if (!Number.isFinite(cost) || cost <= 0) {
      return '取得価格は1以上の数値で入力してください。';
    }
    const usefulLife = Number(values.useful_life_years);
    if (!Number.isFinite(usefulLife) || usefulLife <= 0) {
      return '耐用年数は1以上の数値で入力してください。';
    }
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit(values, asset?.id ?? null);
      setSuccess(mode === 'edit' ? '固定資産を更新しました。' : '固定資産を登録しました。');
      if (mode === 'create') {
        setValues(defaultValues);
        setTouched({ usefulLife: false, kamoku: false });
        setSuggestReason(undefined);
      }
    } catch (err: any) {
      const message = err?.message || '固定資産の保存中にエラーが発生しました。';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        border: '2px inset #d0d0d0',
        padding: '12px',
        backgroundColor: '#ffffff',
        minWidth: '320px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        height: '100%',
      }}
    >
      <div
        style={{
          fontSize: '12px',
          fontWeight: 'bold',
          color: '#003366',
          marginBottom: '4px',
        }}
      >
        {mode === 'edit' ? '固定資産の編集' : '固定資産の登録'}
      </div>

      {error && (
        <div
          style={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            border: '1px solid #c62828',
            padding: '6px',
            fontSize: '11px',
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            backgroundColor: '#e8f5e9',
            color: '#2e7d32',
            border: '1px solid #2e7d32',
            padding: '6px',
            fontSize: '11px',
          }}
        >
          {success}
        </div>
      )}

      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '11px' }}>
        資産名称 *
        <input
          type="text"
          value={values.asset_name}
          onChange={(e) => handleChange('asset_name', e.target.value)}
          style={{ padding: '4px', fontSize: '11px', border: '1px inset #d0d0d0' }}
          disabled={loading || isSubmitting}
          placeholder="例: 通園バス"
        />
        {suggestReason && (
          <span style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
            自動提案: {suggestReason}
          </span>
        )}
      </label>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: '11px' }}>
          取得日 *
          <input
            type="date"
            value={values.acquisition_date}
            onChange={(e) => handleChange('acquisition_date', e.target.value)}
            style={{ padding: '4px', fontSize: '11px', border: '1px inset #d0d0d0' }}
            disabled={loading || isSubmitting}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', fontSize: '11px' }}>
          供用開始日 *
          <input
            type="date"
            value={values.service_start_date}
            onChange={(e) => handleChange('service_start_date', e.target.value)}
            style={{ padding: '4px', fontSize: '11px', border: '1px inset #d0d0d0' }}
            disabled={loading || isSubmitting}
          />
        </label>
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '11px' }}>
        取得価格 (円) *
        <input
          type="number"
          value={values.acquisition_cost}
          onChange={(e) => handleChange('acquisition_cost', e.target.value)}
          style={{ padding: '4px', fontSize: '11px', border: '1px inset #d0d0d0', textAlign: 'right' }}
          disabled={loading || isSubmitting}
          min={1}
        />
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '11px' }}>
        耐用年数 (年) *
        <input
          type="number"
          value={values.useful_life_years}
          onChange={(e) => handleChange('useful_life_years', e.target.value)}
          style={{ padding: '4px', fontSize: '11px', border: '1px inset #d0d0d0', textAlign: 'right' }}
          disabled={loading || isSubmitting}
          min={1}
        />
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '11px' }}>
        設置場所
        <input
          type="text"
          value={values.location}
          onChange={(e) => handleChange('location', e.target.value)}
          style={{ padding: '4px', fontSize: '11px', border: '1px inset #d0d0d0' }}
          disabled={loading || isSubmitting}
          placeholder="例: 第一園舎 車庫"
        />
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '11px' }}>
        減価償却方法
        <input
          type="text"
          value={values.depreciation_method}
          readOnly
          style={{
            padding: '4px',
            fontSize: '11px',
            border: '1px inset #d0d0d0',
            backgroundColor: '#f5f5f5',
          }}
        />
        <span style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>
          ※ Step1では定額法のみサポートしています。
        </span>
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '11px' }}>
        勘定科目 *
        <select
          value={values.kamoku_id}
          onChange={(e) => handleChange('kamoku_id', e.target.value)}
          style={{ padding: '4px', fontSize: '11px', border: '1px inset #d0d0d0' }}
          disabled={loading || isSubmitting}
        >
          <option value="">選択してください</option>
          {kamokuOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', fontSize: '11px' }}>
        備考
        <textarea
          value={values.remarks}
          onChange={(e) => handleChange('remarks', e.target.value)}
          style={{ padding: '4px', fontSize: '11px', border: '1px inset #d0d0d0', minHeight: '60px' }}
          disabled={loading || isSubmitting}
          placeholder="任意でメモを入力できます。"
        />
      </label>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
        {mode === 'edit' ? (
          <>
            <button
              type="button"
              onClick={onCancelEdit}
              style={{
                backgroundColor: '#f0f0f0',
                border: '2px outset #e0e0e0',
                padding: '4px 10px',
                fontSize: '11px',
                cursor: 'pointer',
                minWidth: '80px',
              }}
              disabled={loading || isSubmitting}
            >
              編集を解除
            </button>
            <button
              type="submit"
              style={{
                backgroundColor: '#0066cc',
                color: '#ffffff',
                border: '2px outset #004c99',
                padding: '4px 16px',
                fontSize: '11px',
                cursor: 'pointer',
                minWidth: '100px',
              }}
              disabled={loading || isSubmitting}
            >
              保存
            </button>
          </>
        ) : (
          <button
            type="submit"
            style={{
              backgroundColor: '#0066cc',
              color: '#ffffff',
              border: '2px outset #004c99',
              padding: '4px 16px',
              fontSize: '11px',
              cursor: 'pointer',
              minWidth: '100px',
              marginLeft: 'auto',
            }}
            disabled={loading || isSubmitting}
          >
            登録
          </button>
        )}
      </div>
    </form>
  );
};

export default FixedAssetForm;
